import { IWFNodes } from "@kuika/kuika-cl-model/src/models/workflow";

/**
 * The model↔diagram reconciliation decision, as a pure function (KOA-18136).
 *
 * The workflow exists twice: as the BPMN diagram the author sees, and as the
 * `WorkflowModel` JSON that is PERSISTED and that every consumer resolves a step
 * against — `MAP_INPUT`, `PHASE_ASSIGN`, and the backend's own managed-screen
 * generator (`wfJson.Nodes.Steps.FindNested(...)`, which answers "Step not found."
 * when it misses). The two are kept in step by `syncWorkflowWithDiagram`, and that
 * pass used to be able to DELETE a model node but never to ADD one: nodes entered
 * the model only through the `processPendingChanges` drain, a best-effort side
 * channel that reads a React state snapshot and returns early when it is not
 * committed. Whatever it missed was lost for good.
 *
 * The live consequence: a build whose canvas carried two user tasks persisted a
 * model with one of them, while the flows referencing the missing node were still
 * there — a model that was not stale but internally inconsistent. One user task
 * therefore shipped without its managed screen.
 *
 * This module answers only WHICH diagram elements the model is missing, and in what
 * order they must be adopted. Applying them is the provider's job (it owns the sync
 * service and the subprocess resolution).
 */

/** The minimum a bpmn-js registry element must expose to be reconciled. */
export interface IDiagramElementLike {
  id?: string;
  businessObject?: { $type?: string; id?: string };
}

/**
 * Adoption order is a CONTRACT, not a preference:
 *   • lanes first — `syncTask` reads the target workflow's lanes to resolve a step's
 *     assignment, so a step adopted before its lane loses that assignment;
 *   • containers (SubProcess / CallActivity) before the leaf nodes — the provider
 *     resolves an element's target workflow by walking up to its subprocess STEP, so a
 *     child adopted first would land at the ROOT level instead of inside its subprocess;
 *   • then the leaf nodes;
 *   • sequence flows LAST — a forward is only meaningful once both endpoints exist.
 * Types absent from this list (Participant, Collaboration, …) have no model
 * counterpart; enumerating the adoptable set keeps this decision from depending on
 * the sync service's switch staying silent about them.
 */
export const WORKFLOW_ADOPTION_TYPE_ORDER: readonly string[] = [
  "bpmn:Lane",
  "bpmn:SubProcess",
  "bpmn:CallActivity",
  "bpmn:StartEvent",
  "bpmn:EndEvent",
  "bpmn:BoundaryEvent",
  "bpmn:IntermediateCatchEvent",
  "bpmn:ExclusiveGateway",
  "bpmn:ParallelGateway",
  "bpmn:InclusiveGateway",
  "bpmn:Task",
  "bpmn:UserTask",
  "bpmn:SendTask",
  "bpmn:SequenceFlow"
];

const ADOPTION_RANK_BY_TYPE: ReadonlyMap<string, number> = new Map<string, number>(
  WORKFLOW_ADOPTION_TYPE_ORDER.map((type: string, index: number): [string, number] => [type, index])
);

/** Every node id the model carries, including nested subprocess levels. */
export function collectWorkflowModelNodeIds(nodes: IWFNodes | undefined, out?: Set<string>): Set<string> {
  const ids: Set<string> = out ?? new Set<string>();
  if (!nodes) return ids;

  const collections: (readonly { id?: string }[] | undefined)[] = [
    nodes.steps,
    nodes.gateways,
    nodes.events,
    nodes.lanes,
    nodes.forwards,
    nodes.decisions
  ];
  for (const collection of collections) {
    if (!collection) continue;
    for (const node of collection) {
      if (node?.id) ids.add(String(node.id));
    }
  }

  if (nodes.steps) {
    for (const step of nodes.steps) {
      if (step.subprocess) collectWorkflowModelNodeIds(step.subprocess, ids);
    }
  }
  return ids;
}

/**
 * Diagram elements the model does not carry, in adoption order.
 *
 * A bpmn-js LABEL is a separate registry entry (`<id>_label`) that shares its
 * owner's businessObject, so adopting it would re-run the owner's sync under a
 * foreign id — the businessObject's own id is the reliable discriminator.
 */
export function selectDiagramNodesToAdopt(
  nodes: IWFNodes | undefined,
  diagramElements: readonly IDiagramElementLike[]
): IDiagramElementLike[] {
  const modelNodeIds: Set<string> = collectWorkflowModelNodeIds(nodes);
  const adopted: Set<string> = new Set<string>();
  const candidates: { rank: number; element: IDiagramElementLike }[] = [];

  for (const element of diagramElements) {
    const elementId: string = element?.id === undefined || element.id === null ? "" : String(element.id);
    if (elementId.length === 0) continue;
    if (modelNodeIds.has(elementId) || adopted.has(elementId)) continue;

    const businessObject: { $type?: string; id?: string } | undefined = element.businessObject;
    const rank: number | undefined =
      businessObject?.$type === undefined ? undefined : ADOPTION_RANK_BY_TYPE.get(businessObject.$type);
    if (rank === undefined) continue;
    if (businessObject?.id !== undefined && String(businessObject.id) !== elementId) continue;

    adopted.add(elementId);
    candidates.push({ rank, element });
  }

  // Stable by construction: `sort` keeps insertion order within a rank, so elements of
  // the same type stay in registry order.
  return candidates
    .sort((a: { rank: number }, b: { rank: number }): number => a.rank - b.rank)
    .map((candidate: { element: IDiagramElementLike }): IDiagramElementLike => candidate.element);
}
